/**
 * SC-06 — read the model's `[{"index":1,"text":"…"}]` reply into one card row per shape, keyed on shape id.
 * Returns null when the reply isn't that array (AC-3); a code fence around it is tolerated.
 */

import { toSlideText } from "../../../services/insertIntoSlide";
import type { RephraseTarget, SlideRewrite } from "../types";

/** The array itself: from the first "[" to the last "]", which also drops any code fence around it. */
function unfence(reply: string): string {
  const trimmed = reply.trim();
  const start = trimmed.indexOf("[");
  const end = trimmed.lastIndexOf("]");
  return start >= 0 && end > start ? trimmed.slice(start, end + 1) : trimmed;
}

/**
 * The text as a reader sees it, for deciding whether the AI changed anything (AIVA-9184). The model
 * answers PowerPoint's soft break (\v) with \n, straightens curly quotes and respaces; none of that
 * is a rewrite, and showing it as one confuses.
 */
function asRead(text: string): string {
  return text
    .replace(/\r\n|[\r\v]/g, "\n")
    .replace(/[‘’‛′]/g, "'")
    .replace(/[“”‟″]/g, '"')
    .replace(/[^\S\n]+/g, " ")
    .replace(/ ?\n ?/g, "\n")
    .trim();
}

function isEntry(value: unknown): value is { index: number; text: string } {
  const v = value as { index?: unknown; text?: unknown } | null;
  return typeof v === "object" && v !== null && Number.isInteger(v.index) && typeof v.text === "string";
}

/**
 * One row per target, in slide order (SC-06 AC-1). A shape the model returned unchanged, or left out,
 * is marked `unchanged`: shown on the card, never written.
 */
export function parseRewrites(reply: string, targets: readonly RephraseTarget[]): SlideRewrite[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(unfence(reply));
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length === 0 || !parsed.every(isEntry)) return null;

  const replies = new Map<number, string>();
  for (const { index, text } of parsed) {
    // An index outside the slide, or the same index twice, means the reply can't be trusted.
    if (!targets[index - 1] || replies.has(index)) return null;
    replies.set(index, text);
  }

  return targets.map((target, i) => {
    const original = toSlideText(target.original);
    const text = replies.get(i + 1) ?? "";
    // A blank line the original didn't have pushes text out of a fixed-size shape.
    const rewrite = /\n\s*\n/.test(original) ? toSlideText(text) : toSlideText(text).replace(/\n\s*\n+/g, "\n");
    const changed = rewrite !== "" && asRead(rewrite) !== asRead(original);
    return {
      id: target.id,
      ...(target.groupPath ? { groupPath: target.groupPath } : {}),
      original: target.original,
      rewrite: changed ? rewrite : original,
      ...(changed ? {} : { unchanged: true as const }),
    };
  });
}
